import { useCallback, useState, useRef, useEffect } from 'react';

import { Card, Grid, Radio, Typography } from 'antd';

import { useSectionScroll } from '@/hooks/useSectionScroll';
import { cn } from '@/lib/cn';
import { type Step } from '@/stores/steps';

export type SelectionOption = {
  title: string;
  subtitle?: string;
  itemKey: string;
  steps?: Step[];
  redirectTo?: string;
  disabled?: boolean;
  icon: React.JSXElementConstructor<{ className: string }>;
};

type ProductSelectorProps = {
  list: SelectionOption[];
  onSelect?: (selected: SelectionOption) => void;
  selectedKey?: string;
};

type ProductOptionCardProps = {
  item: SelectionOption;
  index: number;
  isSelected: boolean;
  zIndex: number;
  className?: string;
  bodyClassName?: string;
  onSelect: (item: SelectionOption) => void;
  ref: (element: HTMLDivElement | null) => void;
};

const CARD_OVERLAP_FACTOR = 0.25;
const VERTICAL_OFFSET_FACTOR = 0.2;

const ProductOptionCard = ({
  item,
  index,
  isSelected,
  zIndex,
  className,
  bodyClassName,
  onSelect,
  ref
}: ProductOptionCardProps) => (
  <Card
    size='small'
    id={`product-item-${index}`}
    tabIndex={0}
    style={{ zIndex }}
    ref={ref}
    className={cn(
      'hover:border-primary w-60.5 cursor-pointer transition-all duration-300 select-none',
      {
        'border-primary z-50!': isSelected,
        'border-neutral-300': isSelected && item.disabled
      },
      className
    )}
    classNames={{ body: cn('flex h-full flex-col items-center px-6 py-14', bodyClassName) }}
    onClick={() => onSelect(item)}
    onKeyDown={event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect(item);
      }
    }}
  >
    <Radio value={item.itemKey} checked={isSelected} className='sr-only' />

    <span className='dark:bg-secondary-50/10 mb-6! flex items-center justify-center rounded-full bg-sky-100 p-4'>
      <item.icon className='text-primary h-10 w-10' />
    </span>

    <Typography.Title level={5} className='text-primary mb-0 text-center'>
      {item.title}
    </Typography.Title>
    {item.subtitle && (
      <Typography.Text className='text-content-body text-center text-sm'>{item.subtitle}</Typography.Text>
    )}
  </Card>
);

const ProductSelector = ({ list, onSelect, selectedKey }: ProductSelectorProps) => {
  const [selected, setSelected] = useState<string>(selectedKey || '');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const screens = Grid.useBreakpoint();

  const scrollToItem = useCallback(
    (item: SelectionOption) => {
      const element = itemRefs.current[item.itemKey];

      if (!element || !scrollContainerRef.current || !wrapperRef.current) {
        return;
      }

      const containerDimensions = scrollContainerRef.current.getBoundingClientRect();

      const itemIndex = list.findIndex(listItem => listItem.itemKey === item.itemKey);
      const itemHeight = element.offsetHeight;

      const elementTopPosition = list.slice(0, itemIndex).reduce(heightSum => {
        const itemHeightWithoutMargin = itemHeight - itemHeight * CARD_OVERLAP_FACTOR;
        return heightSum + itemHeightWithoutMargin;
      }, 0);

      const containerCenter = containerDimensions.height / 2;
      const elementCenter = itemHeight / 2;

      const threshold = containerDimensions.height * VERTICAL_OFFSET_FACTOR;
      const translateY = containerCenter - elementCenter - elementTopPosition - threshold;

      wrapperRef.current.style.transform = `translateY(${translateY}px)`;
    },
    [list]
  );

  const handleSelection = useCallback(
    (item: SelectionOption) => {
      setSelected(item.itemKey);
      onSelect?.(item);
      scrollToItem(item);
    },
    [onSelect, scrollToItem]
  );

  useEffect(() => {
    handleSelection(list.find(item => item.itemKey === selectedKey) || list[0]);
  }, [selectedKey, handleSelection, list]);

  const handleScroll = useCallback(
    (direction: 'down' | 'up') => {
      const currentIndex = list.findIndex(item => item.itemKey === selected);

      if (direction === 'down' && currentIndex > 0) {
        const previousItem = list[currentIndex - 1];
        handleSelection(previousItem);
        return;
      }

      if (direction === 'up' && currentIndex < list.length - 1) {
        const nextItem = list[currentIndex + 1];
        handleSelection(nextItem);
      }
    },
    [selected, handleSelection, list]
  );

  useSectionScroll(handleScroll, screens.lg);

  if (screens.lg) {
    return (
      <Radio.Group
        value={selected}
        className='mx-auto mb-30 flex! max-h-fit max-w-197.5 flex-1 flex-wrap justify-center gap-8 transition-transform'
      >
        {list.map((item, index, items) => (
          <ProductOptionCard
            key={`product-${item.itemKey}`}
            item={item}
            index={index}
            isSelected={selected === item.itemKey}
            zIndex={items.length - index}
            className='max-h-76.25'
            onSelect={handleSelection}
            ref={element => {
              itemRefs.current[item.itemKey] = element;
            }}
          />
        ))}
      </Radio.Group>
    );
  }

  return (
    <div ref={scrollContainerRef} className='flex flex-1 flex-col items-center overflow-y-hidden transition-transform'>
      <Radio.Group
        value={selected}
        ref={wrapperRef}
        className='flex! flex-1 flex-col transition-transform duration-300 ease-[cubic-bezier(0.38,1.3,0.68,1)]'
      >
        {list.map((item, index, items) => {
          const isSelected = selected === item.itemKey;

          return (
            <ProductOptionCard
              key={`product-${item.itemKey}`}
              item={item}
              index={index}
              isSelected={isSelected}
              zIndex={items.length - index}
              className={cn('h-76', { 'mt-[-25%] odd:rotate-6 even:-rotate-6': !isSelected })}
              bodyClassName='justify-center'
              onSelect={handleSelection}
              ref={element => {
                itemRefs.current[item.itemKey] = element;
              }}
            />
          );
        })}
      </Radio.Group>
    </div>
  );
};

export default ProductSelector;
